import React, { useState, useEffect } from 'react';
import apiFetch from '../../../utils/apiFetch';
import DatePicker from '../../../components/DatePicker';
import { SectionHead, CardSection, SaveBar } from '../components/ui';
import { FONT } from '../constants';
import { C } from '../styles';
import { authHeaders } from '../helpers';

/* eslint-disable @typescript-eslint/no-explicit-any */
const DatePickerAny = DatePicker as any;
/* eslint-enable @typescript-eslint/no-explicit-any */

type Frequency = 'weekly' | 'bi_weekly' | 'semi_monthly' | 'monthly';

interface ScheduleConfig {
  weekday?: string;
  first_day?: number | string;
  second_day?: number | string;
  day_of_month?: string;
}

interface ScheduleData {
  frequency: Frequency;
  first_payday: string;
  config: ScheduleConfig;
}

const INITIAL: ScheduleData = { frequency: 'semi_monthly', first_payday: '', config: {} };

const FREQS: Array<{ id: Frequency; name: string; meta: string }> = [
  { id: 'weekly', name: 'Weekly', meta: '52 paydays per year - same weekday every week' },
  { id: 'bi_weekly', name: 'Bi-weekly', meta: '26 paydays per year - every 2 weeks' },
  { id: 'semi_monthly', name: 'Semi-monthly', meta: '24 paydays per year - 2 fixed dates per month' },
  { id: 'monthly', name: 'Monthly', meta: '12 paydays per year - one date per month' },
];

const inputStyle: React.CSSProperties = {
  width: '100%', fontFamily: FONT, fontSize: 14, color: C.ink,
  padding: '11px 14px', border: '1.5px solid ' + C.ink, borderRadius: 8,
  background: '#fff', outline: 'none', fontWeight: 500,
};
const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: C.ink, display: 'block', marginBottom: 6 };

export default function PayScheduleSection() {
  const [data, setData] = useState<ScheduleData>(INITIAL);
  const [original, setOriginal] = useState<ScheduleData>(INITIAL);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    apiFetch('/api/v1/payroll/settings', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) {
          const initial: ScheduleData = {
            frequency: (d.default_pay_schedule || 'semi_monthly') as Frequency,
            first_payday: d.pay_period_anchor_date || '',
            config: d.pay_schedule_config || {},
          };
          if (initial.frequency === 'semi_monthly' && (!initial.config.first_day && !initial.config.second_day)) {
            initial.config = { first_day: 15, second_day: 'last' };
          } else if (initial.frequency === 'monthly' && !initial.config.day_of_month) {
            initial.config = { day_of_month: 'last_business' };
          } else if ((initial.frequency === 'weekly' || initial.frequency === 'bi_weekly') && !initial.config.weekday) {
            initial.config = { weekday: 'Friday' };
          }
          setData(initial);
          setOriginal(initial);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const dirty = JSON.stringify(data) !== JSON.stringify(original);

  const isComplete = (): boolean => {
    if (data.frequency === 'weekly' || data.frequency === 'bi_weekly') {
      return Boolean(data.config.weekday && data.first_payday);
    }
    if (data.frequency === 'semi_monthly') {
      return Boolean(data.config.first_day && data.config.second_day);
    }
    if (data.frequency === 'monthly') {
      return Boolean(data.config.day_of_month);
    }
    return false;
  };

  const onSave = async (): Promise<void> => {
    setSaving(true);
    try {
      const res = await apiFetch('/api/v1/payroll/settings', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          default_pay_schedule: data.frequency,
          pay_period_anchor_date: data.first_payday || null,
          pay_schedule_config: data.config || {},
        }),
      });
      if (res.ok) setOriginal(data);
    } catch {
      /* noop */
    }
    setSaving(false);
  };

  const onDiscard = (): void => setData(original);
  const setFreq = (f: Frequency): void => {
    const newConfig: ScheduleConfig = f === 'semi_monthly' ? { first_day: 15, second_day: 'last' }
      : f === 'monthly' ? { day_of_month: 'last_business' }
      : (f === 'weekly' || f === 'bi_weekly') ? { weekday: 'Friday' }
      : {};
    setData({ ...data, frequency: f, config: newConfig });
  };
  const setConfig = <K extends keyof ScheduleConfig>(k: K, v: ScheduleConfig[K]): void => {
    setData({ ...data, config: { ...data.config, [k]: v } });
  };
  const setFirstPayday = (v: string): void => setData({ ...data, first_payday: v });

  if (loading) return <div style={{ color: C.muted, fontSize: 13 }}>Loading...</div>;

  const computeUpcomingPaydays = (): Date[] => {
    if (!isComplete()) return [];
    const today = new Date();
    const paydays: Date[] = [];
    if (data.frequency === 'semi_monthly') {
      const firstDay = parseInt(String(data.config.first_day), 10);
      const secondDay = data.config.second_day;
      const cursor = new Date(today.getFullYear(), today.getMonth(), 1);
      for (let i = 0; i < 6 && paydays.length < 3; i++) {
        const m = new Date(cursor.getFullYear(), cursor.getMonth() + i, 1);
        const d1 = new Date(m.getFullYear(), m.getMonth(), firstDay);
        if (d1 >= today) paydays.push(d1);
        if (paydays.length >= 3) break;
        let d2: Date;
        if (secondDay === 'last') { d2 = new Date(m.getFullYear(), m.getMonth() + 1, 0); }
        else { d2 = new Date(m.getFullYear(), m.getMonth(), parseInt(String(secondDay), 10)); }
        if (d2 >= today) paydays.push(d2);
      }
    } else if (data.frequency === 'monthly') {
      for (let i = 0; i < 6 && paydays.length < 3; i++) {
        const m = new Date(today.getFullYear(), today.getMonth() + i, 1);
        let d: Date;
        if (data.config.day_of_month === 'last' || data.config.day_of_month === 'last_business') {
          d = new Date(m.getFullYear(), m.getMonth() + 1, 0);
          if (data.config.day_of_month === 'last_business') {
            while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
          }
        } else {
          d = new Date(m.getFullYear(), m.getMonth(), parseInt(data.config.day_of_month || '1', 10));
        }
        if (d >= today) paydays.push(d);
      }
    } else if (data.frequency === 'weekly' || data.frequency === 'bi_weekly') {
      if (!data.first_payday) return [];
      const interval = data.frequency === 'weekly' ? 7 : 14;
      const d = new Date(data.first_payday + 'T12:00:00');
      while (d < today) d.setDate(d.getDate() + interval);
      for (let i = 0; i < 3; i++) {
        paydays.push(new Date(d));
        d.setDate(d.getDate() + interval);
      }
    }
    return paydays.slice(0, 3);
  };

  const upcoming = computeUpcomingPaydays();
  const formatDate = (d: Date): string => d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
  const formatFullDate = (d: Date): string => d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric' });
  const daysFromNow = (d: Date): string => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const target = new Date(d); target.setHours(0, 0, 0, 0);
    const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    return 'In ' + diff + ' days';
  };

  return (
    <>
      <SectionHead title="Default pay schedule" subtitle="How often paydays happen and what dates each pay period covers." />

      <div style={{
        background: C.tealSoft, borderLeft: '3px solid ' + C.teal,
        borderRadius: '0 8px 8px 0', padding: '14px 18px', marginBottom: 18,
      }}>
        <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.6 }}>
          <strong style={{ fontWeight: 700, display: 'block', marginBottom: 3 }}>
            Configure how your company pays employees
          </strong>
          Pay schedules vary by country and company. Pick the frequency that matches your existing process.
        </div>
      </div>

      <CardSection label="Pay frequency">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {FREQS.map((f) => {
            const selected = data.frequency === f.id;
            return (
              <div
                key={f.id}
                onClick={() => setFreq(f.id)}
                style={{
                  background: '#fff',
                  border: '1.5px solid ' + (selected ? C.ink : C.line),
                  borderRadius: 10, padding: '16px 18px',
                  cursor: 'pointer',
                  boxShadow: selected ? '0 0 0 3px rgba(10,26,30,.06)' : 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, marginBottom: 2 }}>{f.name}</div>
                    <div style={{ fontSize: 12, color: C.ink }}>{f.meta}</div>
                  </div>
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%',
                    border: '1.5px solid ' + (selected ? C.ink : C.line),
                    background: selected ? C.ink : '#fff',
                    flex: '0 0 18px',
                    display: 'grid', placeItems: 'center', marginTop: 1,
                  }}>
                    {selected && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardSection>

      <CardSection label={
        data.frequency === 'semi_monthly' ? 'Semi-monthly schedule'
        : data.frequency === 'monthly' ? 'Monthly schedule'
        : data.frequency === 'weekly' ? 'Weekly schedule' : 'Bi-weekly schedule'
      }>
        {(data.frequency === 'weekly' || data.frequency === 'bi_weekly') && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Payday weekday</label>
              <select value={data.config.weekday || ''} onChange={(e) => setConfig('weekday', e.target.value)} style={inputStyle}>
                <option value="">Select day...</option>
                <option>Monday</option><option>Tuesday</option><option>Wednesday</option><option>Thursday</option><option>Friday</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>First payday under this schedule</label>
              <DatePickerAny value={data.first_payday} onChange={setFirstPayday} />
            </div>
          </div>
        )}
        {data.frequency === 'semi_monthly' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={labelStyle}>First payday of the month</label>
              <select value={String(data.config.first_day || '')} onChange={(e) => setConfig('first_day', e.target.value)} style={inputStyle}>
                <option value="">Select day...</option>
                {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Second payday of the month</label>
              <select value={String(data.config.second_day || '')} onChange={(e) => setConfig('second_day', e.target.value)} style={inputStyle}>
                <option value="">Select day...</option>
                {[16,17,18,19,20,21,22,23,24,25,26,27,28,29,30].map((n) => <option key={n} value={n}>{n}</option>)}
                <option value="last">Last day of month</option>
              </select>
            </div>
          </div>
        )}
        {data.frequency === 'monthly' && (
          <div>
            <label style={labelStyle}>Payday each month</label>
            <select value={data.config.day_of_month || ''} onChange={(e) => setConfig('day_of_month', e.target.value)} style={inputStyle}>
              <option value="">Select day...</option>
              <option value="1">1st of the month</option>
              <option value="15">15th of the month</option>
              <option value="last_business">Last business day of the month</option>
              <option value="last">Last calendar day of the month</option>
            </select>
          </div>
        )}
      </CardSection>

      <CardSection label="Upcoming paydays">
        {!isComplete() ? (
          <div style={{ padding: '20px', textAlign: 'center', color: C.ink, fontSize: 13 }}>
            <div style={{ fontWeight: 700, marginBottom: 3 }}>Complete the fields above</div>
            <div>We will show your next 3 paydays here.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(' + Math.min(upcoming.length, 3) + ', 1fr)', gap: 12 }}>
            {upcoming.map((d, i) => (
              <div key={i} style={{
                padding: '12px 14px',
                background: i === 0 ? C.tealSoft : C.lineSoft,
                borderRadius: 8,
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.ink, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
                  {i === 0 ? 'Next' : 'Then'}
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.ink, marginBottom: 2 }}>{formatDate(d)}</div>
                <div style={{ fontSize: 11, color: C.ink }}>{formatFullDate(d)}</div>
                <div style={{
                  fontSize: 11, color: C.ink, fontWeight: 600,
                  marginTop: 6, paddingTop: 6,
                  borderTop: '1px solid rgba(0,0,0,0.08)',
                }}>
                  {daysFromNow(d)}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardSection>

      <SaveBar dirty={dirty} saving={saving} onSave={onSave} onDiscard={onDiscard} />
    </>
  );
}